import { AuthFetch, useUser } from "@p0security/frontend-core";
import { FirestoreDoc } from "@p0security/frontend-core/react";
import { Finding } from "@p0security/shared/types/assessment/finding";
import { FindingsContext } from "components/Assessment/contexts/FindingsContext";
import { SelectedEnvironmentContext } from "components/Environment/contexts/SelectedEnvironmentContext";
import { updateDoc } from "firebase/firestore";
import { useObjectMemo } from "hooks/useObjectMemo";
import { mapValues } from "lodash";
import { useCallback, useContext, useMemo } from "react";

type FindingsStateCallbacks = {
  /** Marks one or more findings as "ignored" */
  ignoreFindings: (
    findings: FirestoreDoc<Finding>[],
    justification?: string
  ) => Promise<void>;

  /** Marks one or more findings as "open" or "resolved"
   *
   * End state is chosen based on whether the finding would be open in
   * the most recent run
   */
  unignoreFindings: (findings: FirestoreDoc<Finding>[]) => Promise<void>;

  /** Assigns one or more findings to users */
  assignFindings: (
    findings: FirestoreDoc<Finding>[]
  ) => Promise<Response | undefined>;

  /** Begins managing one or more findings */
  manageFindings: (
    findings: FirestoreDoc<Finding>[],
    secret: string,
    owner: string
  ) => Promise<Response | undefined>;

  /** Unassigns one or more findings from users */
  unassignFindings: (
    findings: FirestoreDoc<Finding>[]
  ) => Promise<Response | undefined>;

  /** Updates notes for one or more findings without changing state */
  updateFindingsNotes: (
    findings: FirestoreDoc<Finding>[],
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => Promise<void>;
};

/** Provides callbacks for mutating finding state & justification
 */
export function useFindingsStateUpdates(): Omit<
  FindingsStateCallbacks,
  "assignFindings" | "manageFindings" | "unassignFindings"
>;
export function useFindingsStateUpdates(
  authFetch: AuthFetch
): FindingsStateCallbacks;
export function useFindingsStateUpdates(authFetch?: AuthFetch): Omit<
  FindingsStateCallbacks,
  "assignFindings" | "manageFindings" | "unassignFindings"
> & {
  assignFindings: FindingsStateCallbacks["assignFindings"] | undefined;
  manageFindings: FindingsStateCallbacks["manageFindings"] | undefined;
  unassignFindings: FindingsStateCallbacks["unassignFindings"] | undefined;
} {
  const { last } = useContext(SelectedEnvironmentContext);
  const { refresh: refreshSelectedMonitor } = useContext(FindingsContext);
  const { user } = useUser();

  const ignoreFindings = useCallback<FindingsStateCallbacks["ignoreFindings"]>(
    async (findings, justification) => {
      const promises = findings.map(
        async (f) =>
          await updateDoc(f.ref, {
            state: "ignored",
            "ignore.at": Date.now(),
            "ignore.by": user?.email ?? "(unknown)",
            ...(justification ? { "ignore.justification": justification } : {}),
          })
      );
      await Promise.all(promises);
    },
    [user?.email]
  );

  const unignoreFindings = useCallback<
    FindingsStateCallbacks["unignoreFindings"]
  >(async (findings) => {
    const promises = findings.map(async (f) => {
      const { trigger } = f.data;
      await updateDoc(f.ref, {
        state: trigger.type === "fix" ? "resolved" : "open",
      });
    });
    await Promise.all(promises);
  }, []);

  const updateFindingsNotes = useCallback<
    FindingsStateCallbacks["updateFindingsNotes"]
  >(async (findings, event) => {
    const promises = findings.map(
      async (f) =>
        await updateDoc(f.ref, {
          "ignore.justification": event.target.value,
        })
    );
    await Promise.all(promises);
  }, []);

  const getAssignmentRequest = useMemo(
    () => (findings: FirestoreDoc<Finding>[]) => {
      const job = last.doc?.data;
      if (!job) {
        console.warn("No assessment job loaded");
        return;
      }
      if (findings.length === 0) return;
      const { monitorId } = findings[0].data;

      return {
        job,
        request: { findingIds: findings.map((f) => f.id), monitorId },
      };
    },
    [last]
  );

  const manageFindings = useMemo(
    () =>
      authFetch
        ? async (
            findings: FirestoreDoc<Finding>[],
            secret: string,
            owner: string
          ) => {
            const job = last.doc?.data;

            if (findings.length === 0 || !job) return;
            const request = {
              findingId: findings[0].id,
              secret,
              owner,
            };
            return await authFetch(
              `assessment/${job.assessmentId}/management/scopes/${findings[0].data.scopeKey}/key-secrets/from-finding`,
              {
                method: "POST",
                json: request,
              }
            );
          }
        : undefined,
    [authFetch, last]
  );

  const assignFindings = useMemo(
    () =>
      authFetch
        ? async (findings: FirestoreDoc<Finding>[]) => {
            const request = getAssignmentRequest(findings);
            if (!request) return;
            return await authFetch(
              `assessment/${request.job.assessmentId}/assignment/start`,
              {
                method: "POST",
                json: request?.request,
              }
            );
          }
        : undefined,
    [getAssignmentRequest, authFetch]
  );

  const unassignFindings = useMemo(
    () =>
      authFetch
        ? async (findings: FirestoreDoc<Finding>[]) => {
            const request = getAssignmentRequest(findings);
            if (!request) return;
            return await authFetch(
              `assessment/${request.job.assessmentId}/assignment/remove`,
              {
                method: "POST",
                json: request?.request,
              }
            );
          }
        : undefined,
    [getAssignmentRequest, authFetch]
  );

  const callbacks = useObjectMemo({
    manageFindings,
    assignFindings,
    ignoreFindings,
    unassignFindings,
    unignoreFindings,
  });

  const refreshCallbacks = useMemo(
    () =>
      mapValues(callbacks, (fn: any) => {
        if (!fn) return fn;
        return async (...args: any[]) => {
          const result = await fn(...args);
          refreshSelectedMonitor();
          return result;
        };
      }),
    [callbacks, refreshSelectedMonitor]
  );

  return useObjectMemo({ ...refreshCallbacks, updateFindingsNotes });
}
